import { Link, useParams } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { ChevronDown, ChevronUp, Lock, Plus } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import {
  useCreateTask,
  useReorderTask,
  useTasks,
  useTimeline,
  useUpdateTask,
} from "@/queries/tasks";
import { useBillingSummary, useUserBilling } from "@/queries/billing";
import { useUser } from "@/queries/user";
import { useUserProjects } from "@/queries/orgs";
import type {
  BillingSummary,
  Task,
  TaskLabel,
  TaskStatus,
  TimelineEntry,
} from "@/types";

function firstOfNextMonth(): string {
  const d = new Date();
  const next = new Date(d.getFullYear(), d.getMonth() + 1, 1);
  return next.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

interface CreditGate {
  blocked: boolean;
  reason: string | null;
  resetAt: string | null;
}

function evaluateGate(
  summary: BillingSummary | undefined,
  scope: "user" | "org",
): CreditGate {
  if (!summary) return { blocked: false, reason: null, resetAt: null };
  if (summary.read_only) {
    return {
      blocked: true,
      reason:
        "Billing is past due. Settle the outstanding invoice to resume task creation.",
      resetAt: null,
    };
  }
  if (scope === "user" && summary.plan === "free") {
    const used = summary.tasks_in_cycle;
    const limit = summary.free_task_limit;
    if (used >= limit) {
      return {
        blocked: true,
        reason: `Free tier exhausted - ${used}/${limit} tasks used this month.`,
        resetAt: firstOfNextMonth(),
      };
    }
  }
  if (
    summary.max_spend_cents != null &&
    summary.estimated_total_cents >= summary.max_spend_cents
  ) {
    return {
      blocked: true,
      reason:
        "Spending cap reached for this cycle. Raise the cap on the billing page to continue.",
      resetAt: null,
    };
  }
  return { blocked: false, reason: null, resetAt: null };
}
import { BlurredEmail } from "@/components/blurred-email";
import { cn } from "@/lib/utils";
import { getErrorMessage } from "@/utils/errors";

const LABELS: TaskLabel[] = ["IDEA", "TODO", "TRIAGE"];
const STATUSES: TaskStatus[] = [
  "PENDING",
  "IN_PROGRESS",
  "COMPLETED",
  "FAILED",
  "DROPPED",
];
const TITLE_MAX = 200;
const BODY_MAX = 400;

function statusVariant(s: TaskStatus) {
  switch (s) {
    case "PENDING":
      return "outline" as const;
    case "IN_PROGRESS":
      return "info" as const;
    case "COMPLETED":
      return "success" as const;
    case "FAILED":
      return "destructive" as const;
    case "DROPPED":
      return "secondary" as const;
  }
}

export function ProjectPage() {
  const { projectId, orgId } = useParams({ strict: false }) as {
    projectId?: string;
    orgId?: string;
  };
  if (!projectId) {
    return (
      <div className="text-sm text-destructive">Project ID missing in URL.</div>
    );
  }
  return <ProjectPageInner projectId={projectId} orgId={orgId ?? null} />;
}

function ProjectPageInner({
  projectId,
  orgId,
}: {
  projectId: string;
  orgId: string | null;
}) {
  const [statusFilter, setStatusFilter] = useState<TaskStatus[]>([]);
  const [labelFilter, setLabelFilter] = useState<TaskLabel[]>([]);
  const [sortBy, setSortBy] = useState<
    "status" | "position" | "created_at" | "updated_at"
  >("status");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const tasks = useTasks(projectId, {
    statuses: statusFilter,
    labels: labelFilter,
  });
  const create = useCreateTask(projectId);
  const reorder = useReorderTask(projectId);
  const user = useUser();
  const userId = user.data?.user_id ?? "";
  const userProjects = useUserProjects(userId);
  const projectName = userProjects.data?.find((p) => p.id === projectId)?.name;
  const orgRole = user.data?.organizations.find(
    (m) => m.org_id === orgId,
  )?.role;
  const userBilling = useUserBilling(orgId ? "" : userId);
  const orgBilling = useBillingSummary(
    orgId && orgRole === "owner" ? orgId : "",
  );
  const gate = orgId
    ? orgRole === "owner"
      ? evaluateGate(orgBilling.data, "org")
      : { blocked: false, reason: null, resetAt: null }
    : evaluateGate(userBilling.data, "user");
  const [selected, setSelected] = useState<Task | null>(null);

  const titleRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [label, setLabel] = useState<TaskLabel>("TODO");

  const rawList = tasks.data ?? [];
  const taskList = [...rawList].sort((a, b) => {
    if (sortBy === "status") {
      const rank = (t: Task) => (t.status === "PENDING" ? 0 : 1);
      const byStatus = rank(a) - rank(b);
      if (byStatus !== 0) return byStatus;
      return (
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      );
    }
    if (sortBy === "position") return a.position - b.position;
    const cmp = new Date(a[sortBy]).getTime() - new Date(b[sortBy]).getTime();
    return sortDir === "asc" ? cmp : -cmp;
  });
  const filtersActive = statusFilter.length > 0 || labelFilter.length > 0;

  const toggle = <T,>(arr: T[], v: T): T[] =>
    arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];

  return (
    <div className="space-y-6">
      <Helmet><title>{projectName ? `TodoLLM - ${projectName}` : "TodoLLM"}</title></Helmet>
      <header className="space-y-1">
        <h2 className="text-3xl font-semibold tracking-tight">Tasks</h2>
        <p className="text-sm text-muted-foreground">
          Updates appear within about 15 seconds via polling.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>New task</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (!title.trim() || gate.blocked) return;
              create.mutate(
                {
                  title: title.trim(),
                  description: description || undefined,
                  label,
                },
                {
                  onSuccess: () => {
                    setTitle("");
                    setDescription("");
                    titleRef.current?.focus();
                  },
                },
              );
            }}
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="flex flex-1 flex-col gap-1.5">
                <Label htmlFor="title">Title</Label>
                <Input
                  ref={titleRef}
                  id="title"
                  placeholder="Refactor auth middleware"
                  value={title}
                  maxLength={TITLE_MAX}
                  onChange={(e) => setTitle(e.target.value)}
                  tabIndex={1}
                />
              </div>
              <div className="flex flex-col gap-1.5 sm:w-40">
                <Label htmlFor="label">Label</Label>
                <Select
                  value={label}
                  onValueChange={(v) => setLabel(v as TaskLabel)}
                >
                  <SelectTrigger id="label" className="w-full" tabIndex={3}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LABELS.map((l) => (
                      <SelectItem key={l} value={l}>
                        {l}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {gate.blocked ? (
                <Button type="button" asChild>
                  <Link
                    to={orgId ? "/orgs/$orgId/billing" : "/billing"}
                    params={orgId ? { orgId } : undefined}
                  >
                    Upgrade to Pro
                  </Link>
                </Button>
              ) : (
                <Button type="submit" disabled={create.isPending} tabIndex={4}>
                  <Plus className="size-4" />
                  Add
                </Button>
              )}
            </div>
            <p className="text-[10px] text-muted-foreground">
              {title.length}/{TITLE_MAX}
            </p>
            <Textarea
              placeholder="Description (optional)"
              rows={2}
              value={description}
              maxLength={BODY_MAX}
              onChange={(e) => setDescription(e.target.value)}
              disabled={gate.blocked}
              tabIndex={2}
            />
            <p className="text-[10px] text-muted-foreground">
              {description.length}/{BODY_MAX}
            </p>
            {gate.blocked && gate.reason && (
              <p className="text-sm text-muted-foreground">
                {gate.reason}
                {gate.resetAt ? ` Resets on ${gate.resetAt}.` : ""}
              </p>
            )}
            {create.error && (
              <p className="text-sm text-destructive">
                {getErrorMessage(create.error)}
              </p>
            )}
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-4">
            <CardTitle>Filter</CardTitle>
            <div className="flex shrink-0 items-center gap-2">
              <Select
                value={sortBy}
                onValueChange={(v) => setSortBy(v as typeof sortBy)}
              >
                <SelectTrigger className="h-7 w-36 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="status">Pending first</SelectItem>
                  <SelectItem value="position">Manual order</SelectItem>
                  <SelectItem value="created_at">Created</SelectItem>
                  <SelectItem value="updated_at">Updated</SelectItem>
                </SelectContent>
              </Select>
              {(sortBy === "created_at" || sortBy === "updated_at") && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() =>
                    setSortDir((d) => (d === "asc" ? "desc" : "asc"))
                  }
                >
                  {sortDir === "asc" ? "Oldest first" : "Newest first"}
                </Button>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {STATUSES.map((s) => {
              const active = statusFilter.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatusFilter((arr) => toggle(arr, s))}
                  className="cursor-pointer"
                  aria-pressed={active}
                >
                  <Badge variant={active ? statusVariant(s) : "outline"}>
                    {s}
                  </Badge>
                </button>
              );
            })}
            {LABELS.map((l) => {
              const active = labelFilter.includes(l);
              return (
                <button
                  key={l}
                  type="button"
                  onClick={() => setLabelFilter((arr) => toggle(arr, l))}
                  className="cursor-pointer"
                  aria-pressed={active}
                >
                  <Badge
                    variant={active ? "default" : "outline"}
                    className="font-mono"
                  >
                    {l}
                  </Badge>
                </button>
              );
            })}
            {filtersActive && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setStatusFilter([]);
                  setLabelFilter([]);
                }}
              >
                Clear
              </Button>
            )}
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>All tasks ({taskList.length})</CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          <div className="overflow-x-auto">
          <div className="grid grid-cols-[minmax(8rem,1fr)_10rem_5rem_7rem_8rem_4rem] items-center gap-3 border-b border-border px-6 pb-2 text-xs uppercase tracking-wider text-muted-foreground">
            <span>Title</span>
            <span>By</span>
            <span>Label</span>
            <span>Status</span>
            <span>Updated</span>
            <span />
          </div>
          {tasks.isLoading && (
            <p className="px-6 pt-3 text-sm text-muted-foreground">
              Loading...
            </p>
          )}
          <ul>
            {taskList.map((t, idx) => (
              <li key={t.id}>
                <div className="grid w-full grid-cols-[minmax(8rem,1fr)_10rem_5rem_7rem_8rem_4rem] items-center gap-3 border-b border-border px-6 py-2.5">
                  <button
                    type="button"
                    onClick={() => setSelected(t)}
                    className="flex cursor-pointer items-center gap-1.5 truncate text-left text-sm font-medium transition-colors hover:text-foreground/70"
                  >
                    {t.is_blocked && (
                      <Lock
                        className="size-3 shrink-0 text-muted-foreground"
                        aria-label="Blocked by dependencies"
                      />
                    )}
                    <span className="truncate">{t.title}</span>
                  </button>
                  <span className="truncate text-xs text-muted-foreground">
                    {t.created_by_name}
                  </span>
                  <Badge variant="outline" className="font-mono">
                    {t.label}
                  </Badge>
                  <Badge variant={statusVariant(t.status)}>{t.status}</Badge>
                  <span className="text-xs text-muted-foreground">
                    {new Date(t.updated_at).toLocaleDateString("en-GB")}
                  </span>
                  <div className="flex flex-col items-center">
                    <button
                      type="button"
                      disabled={
                        idx === 0 || reorder.isPending || sortBy !== "position"
                      }
                      onClick={() =>
                        reorder.mutate({
                          taskId: t.id,
                          position: t.position - 1,
                        })
                      }
                      className="rounded p-0.5 text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
                      title="Move up"
                    >
                      <ChevronUp className="size-3.5" />
                    </button>
                    <button
                      type="button"
                      disabled={
                        idx === taskList.length - 1 ||
                        reorder.isPending ||
                        sortBy !== "position"
                      }
                      onClick={() =>
                        reorder.mutate({
                          taskId: t.id,
                          position: t.position + 1,
                        })
                      }
                      className="rounded p-0.5 text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
                      title="Move down"
                    >
                      <ChevronDown className="size-3.5" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          {taskList.length === 0 && !tasks.isLoading && (
            <p className="px-6 pt-3 text-sm text-muted-foreground">
              No tasks yet.
            </p>
          )}
          </div>
        </CardContent>
      </Card>

      {selected && (
        <TaskDetail
          task={selected}
          allTasks={taskList}
          open={!!selected}
          onOpenChange={(o) => !o && setSelected(null)}
        />
      )}
    </div>
  );
}

interface TaskDetailProps {
  task: Task;
  allTasks: Task[];
  open: boolean;
  onOpenChange: (o: boolean) => void;
}

function TaskDetail({ task, allTasks, open, onOpenChange }: TaskDetailProps) {
  const timeline = useTimeline(task.id);
  const update = useUpdateTask(task.id, task.project_id);
  const [note, setNote] = useState("");
  const [status, setStatus] = useState<TaskStatus>(task.status);

  const blockedBy = task.depends_on
    .map((id) => allTasks.find((t) => t.id === id))
    .filter((t): t is Task => t !== undefined);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {task.is_blocked && (
              <Lock
                className="size-4 shrink-0 text-muted-foreground"
                aria-label="Blocked by dependencies"
              />
            )}
            {task.title}
          </DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="font-mono">
              {task.label}
            </Badge>
            <Badge variant={statusVariant(task.status)}>{task.status}</Badge>
            <span className="text-xs text-muted-foreground">
              created by {task.created_by_name}
            </span>
            {task.claimed_by && (
              <span className="text-xs text-muted-foreground">
                claimed: {task.claimed_by.slice(0, 8)}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {task.description && (
          <p className="text-sm text-foreground/80">{task.description}</p>
        )}

        {blockedBy.length > 0 && (
          <div className="rounded-md border border-border bg-muted/40 px-3 py-2">
            <p className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Blocked by
            </p>
            <ul className="space-y-0.5">
              {blockedBy.map((dep) => (
                <li key={dep.id} className="flex items-center gap-2 text-sm">
                  <Badge
                    variant={statusVariant(dep.status)}
                    className="text-xs"
                  >
                    {dep.status}
                  </Badge>
                  <span
                    className={cn(
                      dep.status === "COMPLETED" &&
                        "line-through text-muted-foreground",
                    )}
                  >
                    {dep.title}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!note.trim()) return;
            update.mutate({ status, note }, { onSuccess: () => setNote("") });
          }}
        >
          <div className="grid gap-3 sm:grid-cols-[10rem_1fr_auto] sm:items-end">
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={status}
                onValueChange={(v) => setStatus(v as TaskStatus)}
              >
                <SelectTrigger id="status" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="note">Note (&lt;=200 chars)</Label>
              <Input
                id="note"
                value={note}
                maxLength={200}
                onChange={(e) => setNote(e.target.value)}
                placeholder="What changed?"
              />
            </div>
            <Button type="submit" disabled={update.isPending}>
              Append
            </Button>
          </div>
          {update.error && (
            <p className="text-sm text-destructive">
              {getErrorMessage(update.error)}
            </p>
          )}
        </form>

        <Separator />

        <div>
          <h4 className="mb-2 text-sm font-medium">Timeline</h4>
          {timeline.isLoading && (
            <p className="text-xs text-muted-foreground">Loading...</p>
          )}
          <ol className="space-y-2">
            {timeline.data?.map((t) => (
              <li
                key={t.id}
                className="rounded-md border-l-2 border-primary/40 bg-muted/40 px-3 py-2"
              >
                <div className="flex flex-wrap gap-2 text-[0.7rem] uppercase tracking-wider text-muted-foreground">
                  <span>{new Date(t.created_at).toLocaleString()}</span>
                  <span>•</span>
                  <TimelineActor entry={t} />
                  {t.status_from && t.status_to && (
                    <>
                      <span>•</span>
                      <span>
                        {t.status_from} -&gt; {t.status_to}
                      </span>
                    </>
                  )}
                </div>
                <p className="mt-1 text-sm">{t.note}</p>
              </li>
            ))}
          </ol>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TimelineActor({ entry }: { entry: TimelineEntry }) {
  const looksLikeEmail = entry.actor_name.includes("@");
  if (!looksLikeEmail) {
    return <span>{entry.actor_display_name ?? entry.actor_name}</span>;
  }
  return (
    <BlurredEmail
      email={entry.actor_name}
      displayName={entry.actor_display_name}
    />
  );
}
